const buttonVariants = {
  primary: 'border-transparent bg-brand-blue text-white shadow-sm hover:bg-brand-blueHover focus-visible:ring-brand-blue',
  secondary: 'border-border-default bg-white text-text-primary hover:bg-surface-hover focus-visible:ring-brand-blue',
  ghost: 'border-transparent bg-transparent text-text-secondary hover:bg-surface-hover hover:text-text-primary focus-visible:ring-brand-blue',
  success: 'border-transparent bg-success-text text-white hover:bg-green-800 focus-visible:ring-success-text',
  danger: 'border-danger-border bg-danger-bg text-danger-text hover:bg-red-100 focus-visible:ring-danger-text',
};

const buttonSizes = {
  sm: 'min-h-8 px-3 text-[13px]',
  md: 'min-h-9 px-4 text-sm',
  lg: 'min-h-11 px-5 text-[15px]',
};

const badgeVariants = {
  neutral: 'border-slate-200 bg-slate-50 text-slate-700',
  info: 'border-info-border bg-info-bg text-info-text',
  success: 'border-success-border bg-success-bg text-success-text',
  warning: 'border-warning-border bg-warning-bg text-warning-text',
  danger: 'border-danger-border bg-danger-bg text-danger-text',
  purple: 'border-purple-200 bg-purple-50 text-purple-700',
  cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
};

const alertVariants = {
  info: 'border-info-border bg-info-bg text-info-text',
  success: 'border-success-border bg-success-bg text-success-text',
  warning: 'border-warning-border bg-warning-bg text-warning-text',
  danger: 'border-danger-border bg-danger-bg text-danger-text',
};

const cardPadding = {
  compact: 'p-4',
  normal: 'p-5',
  spacious: 'p-6',
};

function cx(...classes) {
  return classes.flat().filter(Boolean).join(' ');
}

export function Button({ as: Component = 'button', variant = 'primary', size = 'md', className = '', loading = false, disabled, children, ...props }) {
  return (
    <Component
      className={cx(
        'touch-target inline-flex items-center justify-center gap-2 rounded-app border font-semibold leading-5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        buttonVariants[variant] || buttonVariants.primary,
        buttonSizes[size] || buttonSizes.md,
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" />}
      {children}
    </Component>
  );
}

export function Card({ className = '', padding = 'normal', children, ...props }) {
  return (
    <section className={cx('rounded-card border border-border-default bg-surface-card shadow-card', cardPadding[padding], className)} {...props}>
      {children}
    </section>
  );
}

export function Badge({ variant = 'neutral', className = '', children, ...props }) {
  return (
    <span className={cx('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold leading-5', badgeVariants[variant] || badgeVariants.neutral, className)} {...props}>
      {children}
    </span>
  );
}

export function Alert({ variant = 'info', title, children, className = '', ...props }) {
  return (
    <div className={cx('rounded-xl border px-3.5 py-3 text-sm', alertVariants[variant] || alertVariants.info, className)} {...props}>
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={title ? 'mt-1 text-[13px] leading-5' : 'text-[13px] leading-5'}>{children}</div>}
    </div>
  );
}

export function EmptyState({ title, description, action, icon = '•', className = '' }) {
  return (
    <div className={cx('flex min-h-56 flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blueSoft text-xl text-brand-blue" aria-hidden="true">
        {icon}
      </div>
      <h3 className="text-base font-semibold text-text-primary">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
